/**
 * Global Request Manager
 * Prevents duplicate API calls across the entire application
 */

class GlobalRequestManager {
  constructor() {
    this.pendingRequests = new Map();
    this.requestTimeouts = new Map();
  }

  /**
   * Create a unique key for a request
   */
  createRequestKey(method, url, params = {}, data = {}) {
    const sortedParams = Object.keys(params).sort().reduce((result, key) => {
      result[key] = params[key];
      return result;
    }, {});
    
    const sortedData = Object.keys(data).sort().reduce((result, key) => {
      result[key] = data[key];
      return result;
    }, {});

    return `${method.toUpperCase()}_${url}_${JSON.stringify(sortedParams)}_${JSON.stringify(sortedData)}`;
  }

  /**
   * Check if a request is already pending
   */
  isRequestPending(method, url, params = {}, data = {}) {
    const key = this.createRequestKey(method, url, params, data);
    return this.pendingRequests.has(key);
  }

  /**
   * Add a request to pending requests
   */
  addPendingRequest(method, url, params = {}, data = {}) {
    const key = this.createRequestKey(method, url, params, data);
    this.pendingRequests.set(key, true);
    
    // Set a timeout to remove the request after 30 seconds (safety net)
    const timeout = setTimeout(() => {
      this.removePendingRequest(method, url, params, data);
    }, 30000);
    
    this.requestTimeouts.set(key, timeout);
  }

  /**
   * Remove a request from pending requests
   */
  removePendingRequest(method, url, params = {}, data = {}) {
    const key = this.createRequestKey(method, url, params, data);
    this.pendingRequests.delete(key);
    
    // Clear the timeout
    const timeout = this.requestTimeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.requestTimeouts.delete(key);
    }
  }

  /**
   * Execute a request with deduplication
   */
  async executeRequest(requestFunction, method, url, params = {}, data = {}) {
    // Check if request is already pending
    if (this.isRequestPending(method, url, params, data)) {
      return null; // Return null to indicate request was skipped
    }

    try {
      // Add to pending requests
      this.addPendingRequest(method, url, params, data);
      
      
      // Execute the request
      const result = await requestFunction();
      
      return result;
    } finally {
      // Always remove from pending requests
      this.removePendingRequest(method, url, params, data);
    }
  }

  /**
   * Clear all pending requests (for cleanup)
   */
  clearAllPendingRequests() {
    this.pendingRequests.clear();
    this.requestTimeouts.forEach(timeout => clearTimeout(timeout));
    this.requestTimeouts.clear();
  }

  /**
   * Get current pending requests (for debugging)
   */
  getPendingRequests() {
    return Array.from(this.pendingRequests.keys());
  }
}

// Create a singleton instance
const globalRequestManager = new GlobalRequestManager();

export default globalRequestManager;
